import React from 'react';
import { ModalSlider } from '@/componentsV1/Modal/ModalSlider';
import { Box } from '@chakra-ui/react';
import { FormBuilder } from '@/components/formBuilder';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_SERVICE } from '../../api/api';
import { handleKeyValuePairOfErrorString } from '@/utils/genericErrorHandling';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { useShowToast } from '@/hooks/useShowToast';
import { KText } from '@/ui-library/components/text';

export const SelectMembers = ({
  onClose = () => {},
  onSubmit = () => {},
  customReport = {
    id: '',
    members: [],
  },
}) => {
  const { id: currentUserId } = useSelector(selectCurrUser);
  const toast = useShowToast();
  const queryClient = useQueryClient();

  const updateAccessMutation = useMutation({
    mutationFn: (data: {
      reportID: string;
      payload: Record<string, unknown>;
    }) => API_SERVICE.updateCustomReport(data['reportID'], data['payload']),
    onSuccess: (response) => processUpdatedAccessResponse(response),
  });

  const processUpdatedAccessResponse = (response) => {
    if (response?.error?.length) {
      const errorMessage = handleKeyValuePairOfErrorString(
        response.error || []
      );
      toast({
        title: errorMessage,
        position: 'top-right',
        status: 'error',
        isClosable: true,
      });
    } else {
      queryClient.setQueryData([`getCustomReports/${customReport?.id}`], {
        data: response,
      });

      queryClient.invalidateQueries({
        queryKey: [`getCustomReports/${customReport?.id}`],
      });
      onSubmit();
    }
  };

  const getMembersWithPermissions = (permission) => {
    const memberIDs = [];
    const members =
      customReport?.members
        ?.filter((member) => member?.access === permission)
        .map((member) => {
          const { id, name } = member?.user || {};
          memberIDs.push(id);
          return {
            label: name,
            value: id,
          };
        }) ?? [];
    return { members, memberIDs };
  };

  // set initial values and default options
  const { members: readMembers, memberIDs: readMembersIDs } =
    getMembersWithPermissions('read');
  const { members: writeMembers, memberIDs: writeMembersIDs } =
    getMembersWithPermissions('write');

  const [initialValues, ,] = useState({
    read_members: readMembersIDs,
    write_members: writeMembersIDs,
  });
  const formRef = useRef(null);

  const formConfig = [
    {
      name: 'read_members',
      label: 'Add viewer(s)',
      type: 'autoComplete',
      options: readMembers,
      isMulti: true,
      useInput: true,
      placeholder: 'Search users',
      loadDefaultOptions: false,
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[permission][]=view_reports&filter_by[status][]=active&filter_value=`,
        processResponse: (res) => {
          const options = [];
          res?.data?.users?.forEach((item) => {
            // skip current user
            if (!userHasBeenIncluded(item.id)) {
              options.push({
                label: `${item?.first_name} ${item?.last_name}`,
                value: item?.id,
              });
            }
          });
          return [...options, ...readMembers];
        },
        httpType: 'v1',
      },
      hint: () => {
        return (
          <KText color="secondary-700" size="xs-medium" mb="15px" mt="-5px">
            A viewer can access the report but cannot make any changes or share
            the report with other users
          </KText>
        );
      },
    },
    {
      name: 'write_members',
      label: 'Add editor(s)',
      type: 'autoComplete',
      options: writeMembers,
      isMulti: true,
      useInput: true,
      placeholder: 'Search users',
      loadDefaultOptions: false,
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[permission][]=view_reports&filter_by[status][]=active&filter_value=`,
        processResponse: (res) => {
          const options = [];
          res?.data?.users?.forEach((item) => {
            // skip current user
            if (!userHasBeenIncluded(item.id)) {
              options.push({
                label: `${item?.first_name} ${item?.last_name}`,
                value: item?.id,
              });
            }
          });
          return [...options, ...writeMembers];
        },
        httpType: 'v1',
      },
      hint: () => {
        return (
          <KText color="secondary-700" size="xs-medium" mb="15px" mt="-5px">
            An editor can make changes to the report but they cannot share it
            with other users or delete it
          </KText>
        );
      },
    },
  ];

  const userHasBeenIncluded = (value) => {
    if (value === currentUserId) {
      return true;
    }
    const { viewMembers, editMembers } = getAddedMembers();
    const members = [...editMembers, ...viewMembers];
    return members?.includes(value);
  };

  const getAddedMembers = () => {
    const viewMembers = formRef?.current?.values?.read_members || [];
    const editMembers = formRef?.current?.values?.write_members || [];
    return { viewMembers, editMembers };
  };

  const hasValidationError = () => {
    const { viewMembers, editMembers } = getAddedMembers();
    const hasError = viewMembers.length === 0 && editMembers.length === 0;
    if (hasError) {
      toast({
        title: 'Please select users',
        status: 'error',
        position: 'top-right',
        isClosable: true,
      });
    }
    return hasError;
  };

  const formSubmit = () => {
    const hasValidation = hasValidationError();
    if (!hasValidation) {
      const { viewMembers, editMembers } = getAddedMembers();
      const payload: {
        member_attributes: Record<string, { access: string }>;
        visibility: string;
      } = {
        member_attributes: {},
        visibility: 'member',
      };
      viewMembers.forEach((memberID) => {
        payload.member_attributes[memberID] = { access: 'read' };
      });
      editMembers.forEach((memberID) => {
        payload.member_attributes[memberID] = { access: 'write' };
      });
      updateAccessMutation.mutate({
        reportID: customReport.id,
        payload,
      });
    }
  };

  const modalOnSubmit = () => {
    formRef.current?.submitForm();
  };

  const modalBody = () => {
    return (
      <Box h="100%" w="100%">
        <FormBuilder
          config={formConfig}
          initialValues={initialValues}
          onSubmit={formSubmit}
          formRef={formRef}
        />
      </Box>
    );
  };

  return (
    <ModalSlider
      header={{
        title: 'Share with selected users',
        subtitle: 'Select the users who should have access to this report',
      }}
      modalBody={modalBody()}
      onClose={onClose}
      onSubmit={modalOnSubmit}
      submitButtonIsLoading={updateAccessMutation.isPending}
    />
  );
};
